import { rem } from 'polished';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { bokehTheme as theme } from '@vimeo/bokeh-theme';
import { ChevronDown } from '@vimeo/iris/icons';

enum KeyCodes {
  Enter = 13,
}

const BigChevronIcon = styled(ChevronDown)`
  width: ${rem(24)};
  height: ${rem(24)};
  margin-right: ${rem(20)};
  path {
    fill: ${theme.colors.gray['200'] as string};
  }
`;

const ExpandableSection = styled.div<{ location: 'minimalFooter' | 'footer' | 'header' }>`
  display: flex;
  flex-direction: column;
  margin: 0 ${rem(20)};
  ${({ location }) =>
    location === 'minimalFooter' &&
    css`
      border-bottom: solid ${rem(1)} ${theme.colors.gray['50']};
    `};
`;

const SectionTitle = styled.div<{
  location: 'minimalFooter' | 'footer' | 'header';
  isExpanded: boolean;
}>`
  padding: ${rem(15)} 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ location, isExpanded }) =>
    css`
      color: ${isExpanded || location === 'header' ? theme.colors.gray['800'] : theme.colors.white};
    `}
  &:hover {
    color: ${theme.colors.gray['200']};
    cursor: pointer;
  }
`;

const Text = styled.p<{ location: string }>`
  letter-spacing: ${rem(0.1)};
  margin: 0;
  font-weight: bold;
  ${({ location }) =>
    css`
      color: ${location === 'footer'
        ? theme.colors.white
        : location === 'minimalFooter'
        ? theme.colors.gray['200']
        : theme.colors.gray['800']};
      font-weight: ${location === 'header' ? 'bold' : 'normal'};
    `}
`;

const Section = styled.div`
  margin: 0;
`;

const ListItem = styled.li`
  color: ${theme.colors.gray['800']};
`;

const List = styled.ul<{ padded?: boolean }>`
  ${({ padded }) => padded && 'padding-left: 20px;'}
`;

interface Props {
  items: React.ReactNode[] | null;
  title: string | React.ReactNode;
  location: 'minimalFooter' | 'footer' | 'header';
  defaultExpanded?: boolean;
  canToggle?: boolean;
  padded?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>, isExpanded: boolean) => void;
}

function ExpandableList({
  title,
  items,
  location,
  defaultExpanded = false,
  canToggle = true,
  padded = false,
  onClick,
}: Props) {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  return (
    <ExpandableSection location={location}>
      <Section>
        <SectionTitle
          location={location}
          isExpanded={isExpanded}
          onClick={(e) => {
            canToggle && setIsExpanded(!isExpanded);
            onClick && onClick(e, isExpanded);
          }}
          tabIndex={0}
          onKeyDown={(e) => (e.keyCode === KeyCodes.Enter ? setIsExpanded(!isExpanded) : null)}
        >
          <Text location={location}>{title}</Text>
          {canToggle ? (
            <BigChevronIcon style={{ transform: `rotate(${isExpanded ? '180deg' : '0'})` }} />
          ) : null}
        </SectionTitle>
      </Section>
      {isExpanded && items ? (
        <List padded={padded}>
          {items.map((item, index) => (
            <Section key={`${title}footer-list-item-${index}`}>
              <ListItem>{item}</ListItem>
            </Section>
          ))}
        </List>
      ) : null}
    </ExpandableSection>
  );
}
export default ExpandableList;
